import { Stack, Button, Badge } from '@mui/material';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

import LoginIcon from 'modules/common/components/Icons/LoginIcon';
import ChatConversationIcon from 'modules/common/components/Icons/ChatConversationIcon';
import GiftIcon from 'modules/common/components/Icons/GiftIcon';
import { useIMCContext } from 'modules/IMC/common/context';
import { useLayoutContext } from 'modules/common/components/Layout/LayoutContext';
import useAuthHandlers from 'modules/Auth/AuthProvider/useAuthHandlers';
import { useImpactAdvocateEnabled } from 'modules/common/hooks/useImpactAdvocateEnabled';
import { DrawerType, DrawerTypes } from 'modules/IMC/common/context/IMCContext.interface';
import HelpAgentIcon from 'modules/common/components/Icons/HelpAgentIcon';
import { FeatureGates } from 'modules/common/constants/featuregates';
import useStatsigGate from 'modules/common/useStatsigGate';

function MessageIconWithBadge({ hasUnreadMessages }: { hasUnreadMessages: boolean }) {
    return (
        <Badge
            sx={{
                justifyContent: 'flex-end',
                width: '16px',
                height: '16px',
                mx: 1,
                '& .MuiBadge-badge': { top: '0px', right: '0px' },
                '& svg': {
                    margin: '0px',
                },
            }}
            color="error"
            variant="dot"
            invisible={!hasUnreadMessages}>
            <ChatConversationIcon fontSize="small" sx={{ color: 'cement.700' }} />
        </Badge>
    );
}

export default function DrawerFooter() {
    const { openDrawer, hasUnreadMessages, openHelpAgentWidget } = useIMCContext();
    const { setIsSidebarHidden } = useLayoutContext();
    const history = useHistory();
    const [isLoginOut, setIsLoginOut] = useState(false);
    const isImpactAdvocateEnabled = useImpactAdvocateEnabled();
    const isProjectInboxEnabled = useStatsigGate(FeatureGates.ProjectInboxPoc);

    const handleOpenInbox = () => {
        openDrawer(DrawerTypes.INBOX);
        setIsSidebarHidden(true);
    };

    const openIMCDrawer = (drawerType: DrawerType) => {
        openDrawer(drawerType);
        setIsSidebarHidden(true);
    };

    const handleGetHelp = () => {
        openHelpAgentWidget({ source: 'get_help_drawer_footer' });
        setIsSidebarHidden(true);
    };

    const { logout } = useAuthHandlers();

    const handleSignOut = () => {
        setIsLoginOut(true);
        logout(async () => {
            history.push('/login');
        });
    };

    const handleReferrals = () => {
        history.push('/referrals');
        setIsSidebarHidden(true);
    };

    return (
        <Stack direction="column" gap={1.5} alignItems="flex-start" marginBottom={2}>
            {isImpactAdvocateEnabled && (
                <Button
                    color="grey"
                    sx={{ color: 'grey.700', '& .MuiButton-startIcon': { marginRight: 2 } }}
                    size="small"
                    onClick={handleReferrals}
                    startIcon={<GiftIcon sx={{ color: 'cement.700' }} />}
                    variant="text">
                    Refer & Earn
                </Button>
            )}
            {isProjectInboxEnabled ? (
                <Button
                    color="grey"
                    size="small"
                    sx={{ color: 'grey.700', '& .MuiButton-startIcon': { marginRight: 2 } }}
                    onClick={handleOpenInbox}
                    startIcon={<ChatConversationIcon sx={{ color: 'cement.700' }} />}
                    variant="text">
                    Inbox
                </Button>
            ) : (
                <>
                    <Button
                        color="grey"
                        sx={{ color: 'grey.700', '& .MuiButton-startIcon': { marginRight: 2 } }}
                        size="small"
                        onClick={handleGetHelp}
                        startIcon={<HelpAgentIcon sx={{ color: 'cement.700' }} />}
                        variant="text">
                        Get Help
                    </Button>
                    <Button
                        color="grey"
                        size="small"
                        sx={{
                            '& .MuiButton-startIcon': {
                                marginLeft: '-10px',
                                marginRight: 1.2,
                            },
                            color: 'grey.700',
                        }}
                        onClick={() => openIMCDrawer(DrawerTypes.MESSAGE_CENTER)}
                        startIcon={<MessageIconWithBadge hasUnreadMessages={hasUnreadMessages} />}
                        variant="text">
                        Messages
                    </Button>
                </>
            )}
            <Button
                color="grey"
                size="small"
                sx={{ color: 'grey.700', '& .MuiButton-startIcon': { marginRight: 2 } }}
                disabled={isLoginOut}
                startIcon={<LoginIcon sx={{ color: 'cement.700' }} />}
                onClick={handleSignOut}
                variant="text">
                Sign out
            </Button>
        </Stack>
    );
}
